import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { WfButton } from "@/components/site/primitives";
import { PRIMARY_NAV, HEADER_ACTIONS } from "@/content/nav";
import { NavDropdown, MobileNavGroup } from "@/components/site/NavDropdown";
import { scrollToId } from "@/lib/scrollTo";
import { cn } from "@/lib/utils";

/**
 * StickyHeader
 * @param {boolean} overlay  When true, header starts transparent over a full-bleed
 *   hero, then turns solid maroon on scroll. Logo and text stay white; the
 *   Donate button is always white with maroon text.
 * @param {boolean} linkToHome  When true (used on sub-pages), the logo and
 *   "Get Involved" CTA navigate back to the homepage instead of smooth-scrolling.
 *
 * Primary navigation is route-based and driven by `content/nav.js`.
 */
export const StickyHeader = ({ overlay = false, linkToHome = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const light = overlay && !scrolled; // transparent header over the hero (text gets a soft shadow)

  const go = (id) => (e) => {
    e.preventDefault();
    scrollToId(id);
  };

  const logoProps = linkToHome
    ? { href: "/" }
    : { href: "#top", onClick: go("top") };

  const linkClass = cn(
    "wf-underline inline-flex min-h-11 items-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
    "text-[rgba(255,253,248,0.92)] hover:text-white",
    light && "[text-shadow:0_1px_10px_rgba(0,0,0,0.35)]"
  );

  return (
    <header
      className={cn(
        "inset-x-0 top-0 z-50 border-b transition-[padding,background-color,box-shadow,border-color] duration-300",
        overlay ? "fixed" : "sticky",
        light
          ? "border-transparent bg-transparent"
          : "border-[rgba(255,253,248,0.14)] bg-[var(--wf-maroon)] shadow-[var(--wf-shadow-sm)]"
      )}
    >
      <div
        className={cn(
          "wf-container flex items-center justify-between transition-[height] duration-300",
          scrolled ? "h-16" : "h-20"
        )}
      >
        {/* Logo */}
        <a
          {...logoProps}
          className="flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
          aria-label="World Farmers home"
          data-testid="header-logo-link"
        >
          <img
            src="/images/logo-white.png"
            alt="World Farmers"
            className={cn("w-auto transition-[height] duration-300", scrolled ? "h-9" : "h-11")}
          />
        </a>

        {/* Desktop nav */}
        <nav aria-label="Primary" data-testid="header-nav" className="hidden items-center gap-8 lg:flex">
          {PRIMARY_NAV.map((item) =>
            item.children?.length ? (
              <NavDropdown key={item.id} item={item} light onImage={light} />
            ) : (
              <Link
                key={item.id}
                to={item.href}
                data-testid={`${item.testid}-link`}
                aria-current={pathname === item.href ? "page" : undefined}
                className={cn(linkClass, pathname === item.href && "text-white")}
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-1 sm:gap-3">
          {HEADER_ACTIONS.map((a) => (
            <Link
              key={a.id}
              to={a.href}
              data-testid={a.testid}
              aria-current={pathname === a.href ? "page" : undefined}
              className={cn(linkClass, "hidden md:inline-flex")}
            >
              {a.label}
            </Link>
          ))}
          <WfButton
            as={Link}
            to="/support"
            withArrow
            variant="light"
            className="hidden sm:inline-flex"
            data-testid="header-donate-button"
          >
            Donate
          </WfButton>

          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Open menu"
                data-testid="header-mobile-menu-button"
                className={cn(
                  "inline-flex h-11 w-11 items-center justify-center rounded-lg border lg:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
                  "border-white/40 bg-white/10 text-white backdrop-blur-sm"
                )}
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[86%] max-w-sm overflow-y-auto border-l border-[var(--wf-border)] bg-[var(--wf-cream)]"
              data-testid="mobile-menu-sheet"
            >
              <SheetTitle className="sr-only">Site navigation</SheetTitle>
              <SheetDescription className="sr-only">Browse World Farmers pages and take action.</SheetDescription>
              <div className="mt-2 mb-6">
                <img src="/images/logo-black.png" alt="World Farmers" className="h-10 w-auto" />
              </div>
              <nav aria-label="Mobile">
                <ul className="flex flex-col">
                  {PRIMARY_NAV.map((item) =>
                    item.children?.length ? (
                      <MobileNavGroup
                        key={item.id}
                        item={item}
                        renderParentLink={(parent) => (
                          parent.labelOnly ? (
                            <span
                              data-testid={`mobile-${parent.testid}-link`}
                              className="flex min-h-12 flex-1 items-center text-base font-semibold text-[var(--wf-ink)]"
                            >
                              {parent.label}
                            </span>
                          ) : (
                            <SheetClose asChild>
                              <Link
                                to={parent.href}
                                aria-current={pathname === parent.href ? "page" : undefined}
                                data-testid={`mobile-${parent.testid}-link`}
                                className="flex min-h-12 flex-1 items-center text-base font-semibold text-[var(--wf-ink)] hover:text-[var(--wf-maroon)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                              >
                                {parent.label}
                              </Link>
                            </SheetClose>
                          )
                        )}
                        renderLink={(c, parent) => (
                          <SheetClose asChild>
                            <Link
                              to={c.href}
                              aria-current={pathname === c.href ? "page" : undefined}
                              data-testid={`mobile-nav-${parent.id}-${c.id}-link`}
                              className={cn(
                                "flex min-h-11 items-center rounded-md px-2 text-[15px] font-medium text-[var(--wf-soil)] hover:text-[var(--wf-maroon)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                pathname === c.href && "text-[var(--wf-maroon)]"
                              )}
                            >
                              {c.label}
                            </Link>
                          </SheetClose>
                        )}
                      />
                    ) : (
                      <li key={item.id} className="border-b border-[var(--wf-border)]">
                        <SheetClose asChild>
                          <Link
                            to={item.href}
                            data-testid={`mobile-${item.testid}-link`}
                            className="flex min-h-12 items-center text-base font-semibold text-[var(--wf-ink)] hover:text-[var(--wf-maroon)]"
                          >
                            {item.label}
                          </Link>
                        </SheetClose>
                      </li>
                    )
                  )}
                </ul>
              </nav>
              <div className="mt-6 flex flex-col gap-3">
                <SheetClose asChild>
                  <WfButton as={Link} to="/support" withArrow className="w-full" data-testid="mobile-donate-button">
                    Donate
                  </WfButton>
                </SheetClose>
                <SheetClose asChild>
                  <WfButton as={Link} to="/support" variant="secondary" className="w-full" data-testid="mobile-get-involved-button">
                    Get Involved
                  </WfButton>
                </SheetClose>
                {HEADER_ACTIONS.map((a) => (
                  <SheetClose asChild key={a.id}>
                    <Link to={a.href} data-testid={`mobile-${a.testid}`} className="flex min-h-11 items-center justify-center text-sm font-semibold text-[var(--wf-maroon)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
                      {a.label}
                    </Link>
                  </SheetClose>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
